import {Configuration} from 'priority-web-sdk';
import {handleMessage} from '../../../features/Messages/actions';
import {startMonitor, sendMonitor} from '../Monitoring/monitoring';
import {apiLocker, formsStore} from '../../../stores/Form';
import {wcfclient, odbcclient, priority} from '@priority-software/priority-proto-api/lib';
import {getAdditionalProps} from './loginProps';
import {ABORT, ServerMethods} from './constants';
import {getCompanyName} from '../../../utils/proto-api-utils/utils';
import {getLang} from '../../../Localization';
import {getKey} from '../../wasm/apiService';
import {getWcfLogin} from '../../../_shared/GWTBridge/invoke/genPage';
import {appName, isOpenidLogin} from './loginProps';
import {getTabulaIni, getUserLogin, getUserPassword} from '../../../constants/metaData';

export type MessageHandlers = {
    onClose?: () => void;
    onWarningConfirm?: (res: priority.netitems.form.WarningApproveRs) => void;
    onErrorAccept?: () => void;
    onShowInfo?: (res: priority.netitems.form.IRecordSaveRs) => void;
};

export type Options = {
    withLoader: boolean;
    monitor: boolean;
    loaderTime: number;
    showMessages: boolean;
    cache: boolean;
    messageCallbacks?: MessageHandlers;
    returnFocus: boolean;
    handleMessages?: boolean;
    keepFocus?: boolean;
    canCancel?: boolean;
    select?: number;
};

export const defaultOptions: Options = {
    withLoader: true,
    monitor: true,
    loaderTime: 3000,
    showMessages: true,
    returnFocus: true,
    cache: false,
    handleMessages: true,
    keepFocus: false,
    canCancel: true
};

export const callServer = async (
    method,
    params,
    methodName: ServerMethods,
    options?: Partial<Options>
) => {
    const requestOptions: Options = {
        ...defaultOptions,
        ...(options || {})
    };
    const form = formsStore.currentForm;

    if (requestOptions.canCancel) {
        form?.setAbortController();
    }

    const monitor = requestOptions.monitor && startMonitor(params);
    const {loaderTime, messageCallbacks, handleMessages, keepFocus, withLoader} = requestOptions;
    try {
        apiLocker.lockSession(params?.Session, {loaderTimeout: loaderTime, keepFocus, withLoader});
        const response = await method(params, form?.abortController?.signal);
        const session = params?.Session || response?.Session;

        requestOptions.monitor && sendMonitor(methodName, monitor, response, 'success');
        apiLocker.unlockSession(params?.Session);

        if (handleMessages) {
            await handleMessage(response, session, messageCallbacks);
        }

        return response;
    } catch (err) {
        const {message} = err as Error;
        if (message === ABORT) {
            return;
        }
        sendMonitor(methodName, monitor, {Session: params?.Session}, message);
        alert(message);
    }
};

export const callServerBatch = async (
    method,
    params,
    session: string,
    methodName: ServerMethods,
    options?: Partial<Options>
) => {
    const requestOptions: Options = {
        ...defaultOptions,
        ...(options || {})
    };
    const form = formsStore.getActiveForm();
    if (requestOptions.canCancel) {
        form?.setAbortController();
    }

    const monitor = requestOptions.monitor && startMonitor({...params, Session: session});
    const {loaderTime, messageCallbacks, handleMessages, keepFocus} = requestOptions;
    try {
        apiLocker.lockSession(session, {loaderTimeout: loaderTime, keepFocus});
        const response = await method(params, getAdditionalProps(), form?.abortController?.signal);
        requestOptions.monitor && sendMonitor(methodName, monitor, response, 'success');
        apiLocker.unlockSession(session);

        if (handleMessages) {
            await handleMessage(response, session, messageCallbacks);
        }

        return response;
    } catch (err) {
        const {message} = err as Error;
        if (message === ABORT) {
            return;
        }
        sendMonitor(methodName, monitor, {Session: params?.Session}, message);
        alert(message);
    }
};

export const host =
    process.env.NODE_ENV === 'production'
        ? `https://${window.location.host}`
        : 'https://localhost:3000';

export const API_BASE_URL = (window as any).MyGetURL
    ? (window as any).MyGetURL()
    : `${host}/wcf/wcf/Service.svc`;

export const ODBC_BASE_URL = (window as any).MyGetURL
    ? (window as any).MyGetURL().split('wcf')[0] + 'odbc'
    : `${host}/odbc`;

export const setupBaseUrl = () => {
    wcfclient.setBaseUrl(API_BASE_URL);
};

export const setupOdbcBaseUrl = () => {
    odbcclient.setBaseUrl(ODBC_BASE_URL);
};

export const getLoginConfig: () => Configuration = () => ({
    url: API_BASE_URL,
    tabulaini: getTabulaIni(),
    language: Number(getLang()),
    profile: {company: getCompanyName()},
    username: isOpenidLogin() ? getWcfLogin() : getUserLogin(),
    password: getUserPassword(),
    appid: appName,
    appname: appName,
    appkey: getKey(),
    devicename: ''
});
